import React from 'react'
import {
  propOr,
  prop,
  head,
  flatten,
  isEmpty,
  isNil,
  has,
  includes,
  propEq,
  pathOr,
  uniq,
  equals,
  length,
  compose,
  pick,
  values,
} from 'ramda'
import {
  NONE,
  REMOVED,
  TRUCK_SUBTYPE_42N,
  TRUCK_SUBTYPE_43N,
} from 'utils/constants'
import {
  UNASSIGNED_PANEL_PART,
  DETAILS_SEPERATOR,
  LP017,
  COMBINED_LOCATIONS_PARKING_BREAK_PANEL_NUMBERS,
  LABEL_LP017_AND_LP018,
  STAGED_PANEL_PART,
  AU_PANEL_LOCATION_PREFIX,
  DASH_LABELS,
  AG_PANEL_LOCATION_PREFIX,
} from 'components/SwitchAndGauge/definitions/dashConstants'
import strings from 'utils/strings'
import { isUndefined, isString } from 'utils/types'
import AllParts from 'components/SwitchAndGauge/parts'
import * as NewAllParts from 'components/SwitchAndGauge/parts-components'
import BlankPart from 'components/SwitchAndGauge/Dash/Part/BlankPart'
import DynamicPart from 'components/SwitchAndGauge/Dash/Part/DynamicPart'
import CustomGraphicIcon from 'components/SwitchAndGauge/Dash/Part/CustomGraphicIcon'
import HardwiredSwitchIcon from 'components/SwitchAndGauge/Dash/Part/HardwiredSwitchIcon'
import {
  StyledTooltipParagraph,
  TooltipCustomCapLabel,
  TooltipIconWrapper,
  TooltipTypeRow,
} from 'components/SwitchAndGauge/Dash/Part/Part.style'

const PART_PREFIX = 'Part__'
const BLANK = 'Blank_'
const FILL = 'Fill_'

/**
 * Create blocked list from components and API black list data.
 * @param {[object]} components
 * @param {{BlackListsByLocation}} blacklists
 * @returns {[string]} blockedList
 */
export const createBlockedList = (components = [], blacklists) => {
  const blockedLocations = components.reduce((acc, curr) => {
    const location = prop('location', curr)
    const blockedList = pathOr([], [location, 'blacklist'], blacklists)

    return [...acc, ...blockedList]
  }, [])
  const blockedLocationsUniq = uniq(blockedLocations)

  return blockedLocationsUniq
}

export const isRemovedLocation = includes(REMOVED)
export const isAddedComponent = propEq('hasBeenAdded', true)

export const getLocationIndex = (location = '', isInMsf) => {
  if (location.includes('EA')) return location.substring(2, 5)
  return parseInt(location.substring(isInMsf ? 4 : 2, 5), 10)
}
export const getTypePrefix = (panelNumber = '') =>
  panelNumber.substring(0, 3).replace('-', '')
export const getTypeNumber = (panelNumber = '') => panelNumber.slice(0, -4)
export const getModifiedLocationId = (
  location,
  panelNumber,
  subVehicleType
) => {
  // PBI 55837
  if (
    location === LP017 &&
    subVehicleType === TRUCK_SUBTYPE_43N &&
    COMBINED_LOCATIONS_PARKING_BREAK_PANEL_NUMBERS.includes(panelNumber)
  )
    return LABEL_LP017_AND_LP018
  return location
}
export const getLocationNumericPosition = (
  location = '',
  panelNumber,
  subVehicleType
) => {
  const numericPostion = parseInt(location.substring(2, 5), 10)
  if (equals(numericPostion, NaN)) {
    if (location.includes(UNASSIGNED_PANEL_PART)) {
      return strings.unassigned_part
    }
    return location
  }
  return getModifiedLocationId(location, panelNumber, subVehicleType)
}

export const getAllDraggableLocationsFromLists = (lists) =>
  flatten(
    Object.values(lists).map(({ groups = {} }) =>
      Object.values(groups).reduce(
        (acc, { locations, disableDrag }) =>
          !disableDrag ? acc.concat(locations) : acc,
        []
      )
    )
  )

export const getAllLocationsFromLists = (lists) =>
  flatten(
    Object.values(lists).map(({ groups = {} }) =>
      Object.values(groups).reduce(
        (acc, { locations }) => acc.concat(locations),
        []
      )
    )
  )

/**
 *
 * @param {string} filename filename
 * @param {string} [imageUrl] optional image url
 * @returns {JSX.Element} part component
 */
export const getPartGraphicByFilename = (filename, imageUrl) => {
  if (imageUrl) {
    return DynamicPart
  }
  if (!filename) {
    return BlankPart
  }
  if (filename in NewAllParts) {
    return NewAllParts[filename]
  }
  return AllParts[filename]
}

export const getPanelDefinition = (lists = {}, location = '') =>
  Object.values(lists).find((list) => {
    const { groups } = list
    return Object.values(groups).find(
      ({ locations }) => locations && locations.includes(location)
    )
  })

export const getComponentImageFile = (panelNumber) => {
  if (!panelNumber) return null
  const filename = createPartFileName(panelNumber)
  return getPartGraphicByFilename(filename)
}

export const getLocationPrefix = (location = '', tolower = false) => {
  const prefix = location.substring(0, 2)
  if (tolower) {
    return prefix.toLowerCase()
  }

  return prefix
}

export const removeStagedPrefix = (str = '') =>
  str.replace(`${STAGED_PANEL_PART}_`)

export const getformattedLocation = (listLocation) =>
  !isNil(listLocation) && listLocation.slice(2)

export const getPartType = (panelNumber = '', types = {}) => {
  const typePrefix = getTypeNumber(panelNumber)
  const type = Object.entries(types).find(([key, value]) => value[typePrefix])

  if (type) {
    return head(type)
  }

  return undefined
}

export const placeholderDependency = (key = '', fill = {}) => {
  const group = getLocationPrefix(key, true)
  const fillRules = fill[group]

  if (typeof fillRules === 'function') {
    return fillRules(key)
  }

  return false
}

export const createPartFileName = (panelNumber) => {
  if (!panelNumber) return ''

  const startsWith = panelNumber.slice(0, 3)

  if (['BW', 'CAS', '000'].some((prefix) => startsWith.includes(prefix))) {
    const name = panelNumber.replace(/ /g, '_')
    return `${PART_PREFIX}${name}`
  }

  const name = panelNumber.split('-').join('_').replace(/ /g, '_')
  return `${PART_PREFIX}${name}`
}

export const makeBlankShapeFileName = (vehicleType = '', location = '') =>
  `${PART_PREFIX}${vehicleType}_${location}`

export const makeBlankFileName = (type = '') => `${PART_PREFIX}${BLANK}${type}`

export const makeFillFileName = (type = '') => `${PART_PREFIX}${FILL}${type}`

export const isValidSmartSwitchId = (id) =>
  isString(id) && !isEmpty(id) && id.toLowerCase() !== NONE

/**
 * @param {string} panelNumber panel number
 * @param {string} [location] location
 * @param {string} [panelDescription] panel description
 * @param {boolean} [showPartNumbers] show part numbers
 * @param {string} [capDescription] cap description
 * @param {string} [capPartNumber] cap part number
 * @param {string} [dashObjectName] dash object name
 * @param {string} [dashObjectDescription] dash object description
 * @param {boolean} [isCustomSwitch] is custom switch
 * @param {boolean} [isHardwiredSwitch] is hardwired switch
 * @param {boolean} [isTypeSwitch] is type switch
 * @param {boolean} [isCustomIndicator] is custom indicator
 * @param {boolean} [isCustomHardWiredSwitch] is hardwired switch with custom cap
 * @returns {React.ReactElement}
 */
export const createToolTipContentComponent = (
  panelNumber,
  location,
  panelDescription,
  showPartNumbers,
  capDescription,
  capPartNumber,
  dashObjectName,
  dashObjectDescription,
  isCustomSwitch,
  isHardwiredSwitch,
  isTypeSwitch,
  isCustomIndicator,
  isCustomHardWiredSwitch
) => {
  const tooltipDescription =
    panelDescription || dashObjectDescription || strings.empty

  if (showPartNumbers) {
    return (
      <>
        {`${strings.description}: ${tooltipDescription}`}
        {capDescription && (
          <>
            <br />
            {`${strings.cap_description}: ${capDescription}`}
          </>
        )}
      </>
    )
  }

  const tooltipPanelNumber = tooltipDescription
    ? panelNumber || dashObjectName || ''
    : ''
  return (
    <>
      {isTypeSwitch && (
        <>
          {isCustomHardWiredSwitch ? (
            <TooltipTypeRow>
              <TooltipIconWrapper>
                <CustomGraphicIcon style={{ top: 0, left: 0 }} />
                <HardwiredSwitchIcon />
              </TooltipIconWrapper>
              <TooltipCustomCapLabel>
                {strings.custom_hardwired_switch}
              </TooltipCustomCapLabel>
            </TooltipTypeRow>
          ) : (
            <>
              {(isCustomSwitch ||
                (isCustomIndicator && !isTEMIndicator(panelNumber))) && (
                <CustomGraphicIcon />
              )}
              {isHardwiredSwitch && <HardwiredSwitchIcon />}
              <StyledTooltipParagraph
                hasIcon={
                  isCustomSwitch ||
                  isHardwiredSwitch ||
                  (isCustomIndicator && !isTEMIndicator(panelNumber))
                }
                size="medium"
                weight="bold"
                color="white"
              >
                {isCustomSwitch && !isCustomIndicator && strings.custom_switch}
                {isCustomIndicator &&
                  !isTEMIndicator(panelNumber) &&
                  strings.custom_tem_indicator}
                {isCustomIndicator &&
                  isTEMIndicator(panelNumber) &&
                  strings.tem_indicator}
                {isHardwiredSwitch && strings.hardwired_switch}
                {isTypeSwitch &&
                  !isCustomSwitch &&
                  !isHardwiredSwitch &&
                  !isCustomIndicator &&
                  strings.smart_switch}
              </StyledTooltipParagraph>
            </>
          )}
          <br />
        </>
      )}
      {strings.pos} {getformattedLocation(location)}
      <br />
      {`${strings.description}: ${tooltipDescription}`}
      <br />
      {`${strings.part_number}: ${tooltipPanelNumber}`}
      {capPartNumber && (
        <>
          <br />
          {`${strings.cap_description}: ${capDescription}`}
          <br />
          {`${strings.cap_part_no}: ${capPartNumber}`}
        </>
      )}
    </>
  )
}

export const isPartDraggable = (canEdit, disableDrag, whitelist) =>
  canEdit && !disableDrag && whitelist && whitelist.length > 1

export const hasPartMoved = (
  panelDescription,
  location,
  listLocation,
  fromUnassigned
) =>
  (fromUnassigned && !isUnassignedComponent(location)) ||
  (panelDescription && location !== listLocation)

export const isEmptyPanelLocation = (description) =>
  description === strings.empty

export const inDraggingItemWhitelist = (draggingItem = {}, listLocation = '') =>
  isUnassignedLocation(listLocation) ||
  (has('whitelist', draggingItem) &&
    draggingItem.whitelist.includes(listLocation))

export const canToggleLocations = (draggingItem = {}, whitelist = []) =>
  isUnassignedComponent(prop('location', draggingItem)) ||
  (has('whitelist', draggingItem) && whitelist.includes(draggingItem.location))

export const isAvailableLocation = (
  dragging,
  isEmptyLocation,
  inWhitelist,
  canToggle,
  draggingItem
) =>
  (isUnassignedComponent(prop('location', draggingItem)) && inWhitelist) ||
  (dragging &&
    ((!isEmptyLocation && inWhitelist && canToggle) ||
      (isEmptyLocation && inWhitelist)))

export const isAssignedComponent = (arg) => !isUnassignedLocation(arg)

export const isUnassignedComponent = (arg) => {
  const location = propOr(arg, 'location', arg)
  return isUnassignedLocation(location)
}

export const isUnassignedLocation = (location = '') =>
  location.includes(UNASSIGNED_PANEL_PART)

export const isStagedComponent = (arg) => {
  const location = propOr(arg, 'location', arg)
  return isStagedLocation(location)
}

export const isStagedLocation = (location = '') =>
  location.includes(STAGED_PANEL_PART)

export const isPanelPartSelected = (
  selectedPanelLocation,
  panelLocation,
  panel
) =>
  selectedPanelLocation === panelLocation ||
  (selectedPanelLocation === panel &&
    !isUndefined(panel) &&
    !isUndefined(selectedPanelLocation))

export const getAllPanelLocations = (location, locations) => {
  const allLocations = []

  ;(function recurseFindAllChildren(parentLocation) {
    if (parentLocation) {
      allLocations.push(parentLocation)
      const children = prop('children', parentLocation)

      if (length(children)) {
        const allChildrenLocations = compose(values, pick(children))(locations)
        allLocations.concat(allChildrenLocations)
        allChildrenLocations.forEach(recurseFindAllChildren)
      }
    }
  })(location)

  return allLocations
}

export const joinPanelPartDetails = (detailsArr = []) =>
  detailsArr.join(DETAILS_SEPERATOR)

/**
 * Returns true for plain (non-custom) TEM indicator part numbers.
 * A66-19843-009 and above are plain TEM Indicators.
 * A66-19843-000 through A66-19843-008 are Custom TEM Indicators and will
 * NOT match this function.
 * @param {string} panelNumber
 * @returns {boolean}
 */
export const isTEMIndicator = (panelNumber = '') => {
  const match = panelNumber.match(/^A66-19843-(\d{3})$/)
  return match !== null && parseInt(match[1], 10) >= 9
}

export const getPanelName = (panelName = '') => {
  if (panelName.includes(AU_PANEL_LOCATION_PREFIX)) return DASH_LABELS.AU_PANEL
  if (panelName.includes(AG_PANEL_LOCATION_PREFIX)) return DASH_LABELS.AG_PANEL

  return DASH_LABELS.OL_PANEL
}

export const generateStagedComponentLetter = (num = 0) => {
  let result
  let i = 0
  while (i < num) {
    let letter = ''
    let n = i + 1
    while (n > 0) {
      const rem = n % 26
      if (rem === 0) {
        letter = `Z${letter}`
        n = Math.floor(n / 26) - 1
      } else {
        letter = String.fromCharCode(rem - 1 + 65) + letter
        n = Math.floor(n / 26)
      }
    }
    result = letter
    i += 1
  }
  return result
}

/**
 * @param {object} component component
 * @param {string} [component.panelNumber] component
 * @param {string} [component.dashObjectName] component
 * @returns {string}
 */
export const getComponentName = (component) =>
  propOr('', 'panelNumber', component) || prop('dashObjectName', component)

/**
 * @param {object} component component
 * @param {string} [component.panelDescription] component
 * @param {string} [component.dashObjectDescription] component
 * @returns {string}
 */
export const getComponentDescription = (component) =>
  propOr('', 'panelDescription', component) ||
  prop('dashObjectDescription', component)

/** The canonical plug part number used on 42N vehicles. (#485445) */
export const PLUG_42N_PART_NUMBER = '06-95304-000'

/**
 * Returns the display part number for a component, applying the 42N plug
 * presentation override when appropriate.
 *
 * On 42N vehicles any component whose description identifies it as a plug
 * must display the standardised plug part number instead of the raw
 * component part number, so that every display surface (dashboard graphic,
 * tooltip, drawer header, part-number overlay) is consistent.
 *
 * The raw panelNumber is intentionally preserved for all non-display
 * purposes (API payloads, drag-and-drop identity, useEffect keys, etc.).
 *
 * @param {string} panelNumber       - The component's actual part number.
 * @param {string} vehicleSubType    - The vehicle sub-type string (e.g. '42N').
 * @param {string} [panelDescription] - The component description text.
 * @returns {string} The part number that should be shown in all display surfaces.
 */
export const get42NPlugDisplayNumber = (
  panelNumber,
  vehicleSubType,
  panelDescription
) => {
  if (
    vehicleSubType === TRUCK_SUBTYPE_42N &&
    typeof panelDescription === 'string' &&
    panelDescription.toLowerCase().includes('plug')
  ) {
    return PLUG_42N_PART_NUMBER
  }
  return panelNumber
}
